// APMultiSelect — combobox-style multi-select on shadcn Popover + Command
// (spec § Phase 2 "remaining input gaps": the kit's multi-value picker; Radix
// Select is single-value). Options are static (`options`) or async via Refine
// core `useSelect` (`resource`) with server-side search bound to the v3
// `search eq` contract. `APFormMultiSelect` is the RHF field wrapper.
// Labels / placeholders arrive already translated.
import { AltArrowDownLinear, CheckReadLinear, CloseCircleLinear } from '@assessprep/icons/solar';
import { BaseRecord, CrudFilter, HttpError, useSelect } from '@refinedev/core';
import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFormContext, type FieldPath, type FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { FormControl, FormField } from '@/components/ui/form';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/cn';
import { APFieldBaseProps, APFieldShell, fieldTestId } from './Fields';

const SEARCH_DEBOUNCE_MS = 500; // v3 useCustomSelect parity
const DEFAULT_PAGE_SIZE = 15; // v3 useCustomSelect parity

export interface APMultiSelectOption {
  value: string | number;
  /** Already-translated / display-ready label. */
  label: string;
  /** Dropdown-row rendering when it must differ from `label` (indented tree
   *  rows showing the short part name). Chips and search stay on `label`. */
  content?: ReactNode;
  disabled?: boolean;
  /** End-aligned per-option annotation (state hints, e.g. graded state).
   *  Display-only — search matches on `label` alone. */
  trailing?: ReactNode;
}

export interface APMultiSelectOptionGroup {
  /** Already-translated group heading. Omit for a heading-less run of options
   *  (mixed grouped/loose lists — v3 antd optgroup parity). */
  label?: string;
  options: APMultiSelectOption[];
}

export interface APMultiSelectProps {
  value?: Array<string | number>;
  onChange?: (values: Array<string | number>) => void;
  /** Fired when an option is added (after `onChange`) — v3 OrgSelect `onSelect` parity. */
  onSelect?: (option: APMultiSelectOption) => void;
  /** Fired when a value is removed (badge ✕ or list toggle). */
  onRemove?: (value: string | number) => void;
  /** Fired when the options popover opens or closes — lets a host defer an
   *  expensive options fetch until the user actually opens the list. */
  onOpenChange?: (open: boolean) => void;
  /** Static options. Ignored when `resource` is set. */
  options?: APMultiSelectOption[];
  /** Static grouped options (v3 SubjectSelect optgroup parity). Ignored when
   *  `resource` is set; takes precedence over `options`. */
  groups?: APMultiSelectOptionGroup[];
  /** Refine resource for async options (server-side `search eq` — v3 contract). */
  resource?: string;
  /** Deep accessor into the record, e.g. 'attributes.name' (JSON:API rows). */
  optionLabel?: string;
  optionValue?: string;
  /** Extra permanent filters for the options query. */
  filters?: CrudFilter[];
  /** Async mode only: build the LISTED options from the raw page rows instead
   *  of the per-record accessors — one record may expand to several options
   *  (v3 useOrgSelect district flatten: parent + serialized descendants).
   *  Hydrated-value labels still resolve through the accessors. Pass a stable
   *  (module-level / memoized) function. */
  transformRecords?: (records: BaseRecord[]) => APMultiSelectOption[];
  /** Async mode only: GROUPED variant of `transformRecords` (v3 useOrgSelect
   *  nested_children branch — parent heading, children selectable). Takes
   *  precedence over `transformRecords`. Pass a stable function. */
  transformGroups?: (records: BaseRecord[]) => APMultiSelectOptionGroup[];
  pageSize?: number;
  /** Hydrated ids whose labels must be fetched up-front (refine `useSelect`
   *  defaultValue → getMany) — v3 useCustomSelect defaultValue parity. */
  defaultValue?: Array<string | number>;
  /** Seed labels for hydrated values that may not be in the loaded options page. */
  extraOptions?: APMultiSelectOption[];
  /** `false` → single-select combobox (selection replaces the value and closes). */
  multiple?: boolean;
  /** Visible ✕ inside the trigger (P2 filter affordance — re-pick-to-clear
   *  alone reads as "stuck"). Single mode clears the value; multi mode clears
   *  ALL values at once (run-panel clear-all). Opt-in: required form fields
   *  must keep it off. */
  clearable?: boolean;
  /** Render selected values as badges inside the trigger (multi mode). */
  showBadges?: boolean;
  /** Collapse the trigger chip stack: show the first N value badges then a
   *  non-interactive "+N more" summary (dev 2026-07-16, assign panel; default
   *  2 app-wide per R2-G19, dev 2026-07-23 — the full set stays visible and
   *  editable in the open popover). Pass a number to override the cap. */
  maxBadges?: number;
  /** With `maxBadges`: native `title` on the "+N" badge listing the collapsed
   *  values' labels (kit truncate+title idiom — P2-229, deliver class select
   *  only). Opt-in; default keeps the summary-only badge. */
  overflowTitle?: boolean;
  /** Hide already-selected entries from the dropdown (v3 `hideSelectedInInput`). */
  hideSelected?: boolean;
  /** `top` keeps content under the trigger visible while picking. */
  side?: 'top' | 'bottom';
  /** Replaces the trigger's badge/text content with a caller-rendered summary.
   *  The clear ✕ and chevron affordances stay. */
  triggerContent?: ReactNode;
  /** Fired whenever the value→label pairs resolved for the current `value`
   *  change (selection, hydrated-id label fetch) — lets filter panels thread
   *  labels to surfaces that render filter values elsewhere (APBulkArchive
   *  badges via the collection's filter-label registry). Only resolved values
   *  are included. */
  onLabelsResolved?: (entries: Record<string, string>) => void;
  /** Set by the floating-label field wrapper: the label occupies the resting
   *  slot, so the empty trigger renders no placeholder text and carries
   *  `data-placeholder` for APFloatingField's group tracking. */
  floatingLabel?: boolean;
  placeholder?: string;
  searchPlaceholder?: string;
  /** Already-translated empty-results copy. */
  emptyMessage?: ReactNode;
  disabled?: boolean;
  className?: string;
  /** Accessible name for the trigger when no visible <label> is associated. */
  'aria-label'?: string;
  'data-testid'?: string;
}

const sameValue = (a: string | number, b: string | number) => String(a) === String(b);

const getPath = (record: unknown, path: string): unknown =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  path.split('.').reduce<any>((acc, key) => (acc == null ? undefined : acc[key]), record);

/** The defaultValue query (getMany by_ids) may serialize rows differently
 *  than the list query (orgs: flat list rows, JSON:API by_ids rows) — try the
 *  accessor as given, then with the `attributes.` envelope added/stripped. */
const tolerantPath = (record: unknown, path: string): unknown => {
  const direct = getPath(record, path);
  if (direct != null) return direct;
  return path.startsWith('attributes.')
    ? getPath(record, path.slice('attributes.'.length))
    : getPath(record, `attributes.${path}`);
};

export function APMultiSelect({
  value = [],
  onChange,
  onSelect,
  onRemove,
  onOpenChange,
  options: staticOptions,
  groups: staticGroups,
  resource,
  optionLabel = 'attributes.name',
  optionValue = 'attributes.id',
  filters = [],
  transformRecords,
  transformGroups,
  pageSize = DEFAULT_PAGE_SIZE,
  defaultValue,
  extraOptions = [],
  multiple = true,
  clearable = false,
  showBadges = true,
  maxBadges = 2,
  overflowTitle = false,
  hideSelected = false,
  side = 'bottom',
  triggerContent,
  onLabelsResolved,
  floatingLabel = false,
  placeholder,
  searchPlaceholder,
  emptyMessage,
  disabled,
  className,
  'aria-label': ariaLabel,
  'data-testid': testId = 'ap-multi-select',
}: APMultiSelectProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  // Options fetch is deferred to the FIRST popover open — APFilterPanel keeps
  // its children mounted while closed, so an on-mount fetch would fire on
  // every list-page load even if the picker is never opened.
  const [hasOpened, setHasOpened] = useState(false);
  const [search, setSearch] = useState('');
  const isAsync = !!resource;

  // Hydrated values (URL-restored filters) arrive as bare ids — capture the
  // unlabeled ones once and let useSelect's defaultValue (getMany by_ids[])
  // fetch their labels; otherwise the trigger renders the raw id. Skipped
  // when nothing displays labels (badge-less multi pickers).
  const needsLabels = !multiple || showBadges || !!onLabelsResolved;
  const hydrationSeedRef = useRef<Array<string | number> | null>(null);
  if (hydrationSeedRef.current === null && value.length > 0) {
    const known = new Set(extraOptions.map((option) => String(option.value)));
    hydrationSeedRef.current =
      isAsync && needsLabels ? value.filter((v) => !known.has(String(v))) : [];
  }
  const resolvedDefaultValue = defaultValue ?? hydrationSeedRef.current ?? undefined;

  const {
    options: fetchedOptions,
    onSearch: refineOnSearch,
    query,
  } = useSelect<BaseRecord, HttpError>({
    resource: resource ?? '',
    // Accessor functions so deep paths ('attributes.name') AND the
    // defaultValue query's row shape both resolve (see tolerantPath).
    optionLabel: ((record: BaseRecord) => tolerantPath(record, optionLabel)) as never,
    optionValue: ((record: BaseRecord) => tolerantPath(record, optionValue) ?? record.id) as never,
    // v3 useCustomSelect wire parity: `search eq <value>` + page_size filter.
    // Empty term clears the filter entirely (refine's custom-mapper path
    // doesn't auto-clear like the built-in one) — else the close-reset caches
    // a spurious `search=` key variant.
    onSearch: (searchValue) =>
      searchValue ? [{ field: 'search', operator: 'eq', value: searchValue }] : [],
    filters: [{ field: 'page_size', operator: 'eq', value: pageSize }, ...filters],
    // Hydrated values fetch their labels via getMany (by_ids[]) — v3 parity.
    defaultValue: resolvedDefaultValue as never,
    debounce: SEARCH_DEBOUNCE_MS,
    queryOptions: { enabled: isAsync && hasOpened },
    // Label seeding for hydrated values must NOT wait for the popover —
    // useSelect falls back to queryOptions for this query when unset.
    defaultValueQueryOptions: { enabled: isAsync },
  });

  const listRecords = query?.data?.data;
  const asyncGroups: APMultiSelectOptionGroup[] | null = useMemo(() => {
    if (!isAsync || !transformGroups) return null;
    return transformGroups(listRecords ?? []).map((group) => ({
      ...group,
      options: group.options.map((option) => ({
        ...option,
        label: String(option.label ?? option.value),
      })),
    }));
  }, [isAsync, transformGroups, listRecords]);
  const listedOptions: APMultiSelectOption[] = useMemo(() => {
    if (asyncGroups) return asyncGroups.flatMap((group) => group.options);
    if (!isAsync) return staticGroups?.flatMap((group) => group.options) ?? staticOptions ?? [];
    if (transformRecords) {
      return transformRecords(listRecords ?? []).map((option) => ({
        ...option,
        label: String(option.label ?? option.value),
      }));
    }
    // Derive from the QUERY DATA, not refine's `options` state: that state is
    // populated only in the list query's onSuccess, which never fires on a
    // react-query cache HIT (staleTime Infinity, Providers.tsx) — a remounted
    // picker rendered an empty list until a search forced an uncached key
    // (J22 select-no-data-after-tab-switch).
    return (listRecords ?? []).map((record: BaseRecord) => {
      const v = (tolerantPath(record, optionValue) ?? record.id) as string | number;
      return { value: v, label: String(tolerantPath(record, optionLabel) ?? v) };
    });
  }, [
    asyncGroups,
    isAsync,
    transformRecords,
    listRecords,
    optionLabel,
    optionValue,
    staticGroups,
    staticOptions,
  ]);

  // Async options churn with the search — remember every option ever seen so
  // selected badges keep their labels after the list moves on. Accessor-mapped
  // options feed first so transformRecords' display labels win on overlap, and
  // hydrated-value (getMany) labels resolve even in transform mode.
  const seenLabelsRef = useRef(new Map<string, string>());
  if (isAsync) {
    // Refine's combined options still carry the defaultValue (getMany) labels
    // for hydrated values — seed them in EVERY async mode (listed options are
    // derived from query data above and no longer include them).
    (fetchedOptions as APMultiSelectOption[]).forEach((option) =>
      seenLabelsRef.current.set(String(option.value), String(option.label ?? option.value))
    );
  }
  listedOptions.forEach((option) => seenLabelsRef.current.set(String(option.value), option.label));
  extraOptions.forEach((option) => seenLabelsRef.current.set(String(option.value), option.label));

  const labelFor = (v: string | number) => seenLabelsRef.current.get(String(v)) ?? String(v);

  // Emit resolved value→label pairs when they change (keyed on content, not
  // identity — `value` arrays are typically rebuilt inline every render).
  const resolvedEntries: Record<string, string> = {};
  value.forEach((v) => {
    const label = seenLabelsRef.current.get(String(v));
    if (label !== undefined) resolvedEntries[String(v)] = label;
  });
  const entriesKey = JSON.stringify(resolvedEntries);
  const resolvedEntriesRef = useRef(resolvedEntries);
  resolvedEntriesRef.current = resolvedEntries;
  const onLabelsResolvedRef = useRef(onLabelsResolved);
  onLabelsResolvedRef.current = onLabelsResolved;
  useEffect(() => {
    onLabelsResolvedRef.current?.(resolvedEntriesRef.current);
  }, [entriesKey]);

  const visibleGroups = useMemo(() => {
    const withoutSelected = (options: APMultiSelectOption[]) =>
      hideSelected
        ? options.filter((option) => !value.some((v) => sameValue(v, option.value)))
        : options;
    const groups = asyncGroups ?? (!isAsync ? staticGroups : undefined);
    if (groups) {
      return groups
        .map((group) => ({
          label: group.label as string | undefined,
          options: withoutSelected(group.options),
        }))
        .filter((group) => group.options.length > 0);
    }
    return [{ label: undefined, options: withoutSelected(listedOptions) }];
  }, [asyncGroups, isAsync, staticGroups, listedOptions, hideSelected, value]);

  const handleSearchChange = (next: string) => {
    setSearch(next);
    if (isAsync) refineOnSearch(next);
  };

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (nextOpen) setHasOpened(true);
    onOpenChange?.(nextOpen);
    // Reset IMMEDIATELY (flush past the search debounce) — a stale server
    // filter renders the old term's results on the next open (J21-M06).
    if (!nextOpen && search !== '') {
      setSearch('');
      if (isAsync) {
        refineOnSearch('');
        (refineOnSearch as unknown as { flush?: () => void }).flush?.();
      }
    }
  };

  const removeValue = (v: string | number) => {
    onChange?.(value.filter((current) => !sameValue(current, v)));
    onRemove?.(v);
  };

  const handleToggle = (option: APMultiSelectOption) => {
    const isSelected = value.some((v) => sameValue(v, option.value));
    if (!multiple) {
      if (isSelected) {
        removeValue(option.value); // re-picking the selected option clears it (v3 allowClear parity)
      } else {
        onChange?.([option.value]);
        onSelect?.(option);
      }
      // Real close path — a bare setOpen(false) never fires onOpenChange on a
      // controlled popover, so the search reset would be skipped (J21-M06).
      handleOpenChange(false);
      return;
    }
    if (isSelected) {
      removeValue(option.value);
    } else {
      onChange?.([...value, option.value]);
      onSelect?.(option);
    }
  };

  const hasSelection = value.length > 0;

  // "More results" footer (async only) — tells the user the list is truncated
  // so they type to narrow. Non-interactive; rendered below the option groups.
  // `shownCount` = option ROWS rendered (post hideSelected exclusion) → the
  // "showing" number. Truncation is tested on the RAW server page-row count
  // (`fetchedCount`, pre-exclusion) vs `pageSize`, so hiding already-selected
  // rows can neither falsely trigger nor suppress the hint.
  const shownCount = visibleGroups.reduce((count, group) => count + group.options.length, 0);
  const fetchedCount = listRecords?.length ?? 0;
  const total = query?.data?.total;
  const hasTotal = typeof total === 'number' && Number.isFinite(total) && total > 0;
  const isFetching = !!query?.isFetching;
  // Count form when a real total exceeds what's shown; else the generic form
  // once the fetched page hits the page-size cap (likely more on the server).
  const showCountForm = hasTotal && total > shownCount;
  const truncated = hasTotal ? total > shownCount : fetchedCount === pageSize;
  const showMoreHint = isAsync && !isFetching && shownCount > 0 && truncated;
  // Seeded-option race: on first open the defaultValue query has already
  // seeded the selected row(s) but the list query is still resolving, so the
  // dropdown shows a short, complete-looking list. Signal it's still loading.
  // Mutually exclusive with showMoreHint (which requires !isFetching).
  const showLoadingRow = isAsync && isFetching && shownCount > 0;

  // Visible clear affordance (opt-in) — mirrors APFormDateTime's showClear:
  // shown only while a value is present and clearing is allowed. A SIBLING of
  // the trigger (never nested — a button inside a button is invalid), so
  // clicking it can't toggle the popover. Refocuses the trigger so keyboard
  // users aren't dropped to <body> when the ✕ unmounts. Multi mode clears the
  // whole selection in one emission (`onChange([])` — per-value `onRemove`
  // stays a badge/list affair).
  const clearEnabled = clearable;
  const showClear = clearEnabled && hasSelection && !disabled;
  const triggerRef = useRef<HTMLButtonElement>(null);
  const clearSelection = () => {
    if (multiple) onChange?.([]);
    else removeValue(value[0]);
    triggerRef.current?.focus();
  };

  // v3 antd filterOption parity: static mode matches on the LABEL substring
  // (threaded as keywords) — cmdk's default scorer would also match on
  // `value`, which is the id and must stay identity-only.
  const commandFilter = useCallback(
    (itemValue: string, itemSearch: string, keywords?: string[]) => {
      const haystack = (keywords?.length ? keywords.join(' ') : itemValue).toLowerCase();
      return haystack.includes(itemSearch.trim().toLowerCase()) ? 1 : 0;
    },
    []
  );

  const trigger = (
    <PopoverTrigger asChild>
      <Button
        ref={triggerRef}
        type="button"
        variant="outline"
        role="combobox"
        aria-label={ariaLabel}
        aria-expanded={open}
        // Explicit tabindex: macOS Safari/Firefox skip <button> in Tab order
        // unless the attribute is present (P1-6 create-form tab order).
        tabIndex={0}
        disabled={disabled}
        data-placeholder={hasSelection ? undefined : ''}
        data-testid={testId}
        className={cn(
          'h-auto min-h-9 w-full min-w-0 justify-between px-3 py-1.5 font-normal hover:bg-transparent',
          !hasSelection && 'text-muted-foreground',
          // With the clear affordance the wrapper div is the outer footprint —
          // the caller's sizing must land THERE (an inline `w-44` on the button
          // inside a `w-full` wrapper stacked every clearable filter full-row).
          !clearEnabled && className
        )}
      >
        {triggerContent != null ? (
          <span className="min-w-0 flex-1 truncate text-start">{triggerContent}</span>
        ) : multiple && showBadges && hasSelection ? (
          // Cap the chip stack + scroll inside — an uncapped trigger grows
          // unbounded and shoves the popover down on every add.
          <span className="flex max-h-24 flex-1 flex-wrap gap-1 overflow-y-auto">
            {value.slice(0, maxBadges).map((v) => (
              <Badge
                key={String(v)}
                variant="secondary"
                className="max-w-48"
                data-testid={`${testId}-badge-${v}`}
              >
                {/* title = full label on hover once truncated (MediaItems idiom). */}
                <span className="truncate" title={labelFor(v)}>
                  {labelFor(v)}
                </span>
                {!disabled && (
                  <span
                    role="button"
                    aria-label={t('CommonText.remove')}
                    className="-me-0.5 inline-flex cursor-pointer opacity-60 hover:opacity-100"
                    data-testid={`${testId}-remove-${v}`}
                    onClick={(event) => {
                      // Don't open the popover on badge removal.
                      event.stopPropagation();
                      removeValue(v);
                    }}
                  >
                    <CloseCircleLinear aria-hidden="true" className="size-3.5" />
                  </span>
                )}
              </Badge>
            ))}
            {value.length > maxBadges && (
              // Summary only — the trigger itself opens the dropdown, where
              // every selection stays visible and removable. `overflowTitle`
              // adds a hover title naming the collapsed labels (P2-229).
              <Badge
                variant="secondary"
                title={overflowTitle ? value.slice(maxBadges).map(labelFor).join(', ') : undefined}
                data-testid={`${testId}-badge-overflow`}
              >
                {t('APMultiSelect.overflow_more', { count: value.length - maxBadges })}
              </Badge>
            )}
          </span>
        ) : (
          // min-w-0 + flex-1 let a long value (e.g. a full subject name)
          // ellipsis-truncate inside the trigger; title carries the full name
          // on hover once truncated (MediaItems idiom).
          <span
            className="min-w-0 flex-1 truncate text-start"
            title={!multiple && hasSelection ? labelFor(value[0]) : undefined}
          >
            {!multiple && hasSelection
              ? labelFor(value[0])
              : floatingLabel && !hasSelection
              ? ''
              : placeholder ?? t('CommonText.select')}
          </span>
        )}
        {/* Reserves the exact slot the absolute ✕ overlays (end-9 = px-3 +
              chevron + gap) so the value text never runs underneath it. */}
        {showClear && <span aria-hidden="true" className="size-4 shrink-0" />}
        <AltArrowDownLinear aria-hidden="true" className="size-4 shrink-0 opacity-50" />
      </Button>
    </PopoverTrigger>
  );

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      {clearEnabled ? (
        <div className={cn('relative w-full min-w-0', className)}>
          {trigger}
          {showClear && (
            <button
              type="button"
              onClick={clearSelection}
              aria-label={t('CommonText.clear_selection')}
              className="absolute end-9 top-1/2 flex -translate-y-1/2 items-center rounded-sm text-muted-foreground outline-none hover:text-foreground focus-visible:ring-[3px] focus-visible:ring-ap-ring-a50"
              data-testid={`${testId}-clear`}
            >
              <CloseCircleLinear aria-hidden="true" className="size-4" />
            </button>
          )}
        </div>
      ) : (
        trigger
      )}
      <PopoverContent
        align="start"
        side={side}
        // Content fits the OPTIONS (w-max), never narrower than the trigger,
        // capped at 24rem/90vw — past the cap options wrap instead of truncate
        // (full subject names must stay readable; the trigger still truncates).
        className="w-max min-w-(--radix-popover-trigger-width) max-w-[min(24rem,90vw)] p-0"
        data-testid={`${testId}-content`}
      >
        {/* Async mode searches server-side — cmdk's client filtering must not double-filter. */}
        <Command shouldFilter={!isAsync} filter={commandFilter}>
          <CommandInput
            value={search}
            onValueChange={handleSearchChange}
            placeholder={searchPlaceholder ?? t('CommonText.search')}
          />
          <CommandList>
            <CommandEmpty>
              {isAsync && query?.isFetching
                ? t('CommonText.loading')
                : emptyMessage ?? t('CommonText.no_data')}
            </CommandEmpty>
            {visibleGroups.map((group, groupIndex) => (
              <CommandGroup key={group.label ?? groupIndex} heading={group.label}>
                {group.options.map((option) => {
                  const isSelected = value.some((v) => sameValue(v, option.value));
                  return (
                    <CommandItem
                      key={String(option.value)}
                      // Identity by id, ALWAYS — labels repeat across groups
                      // (terms per academic year, subjects per group) and a
                      // label-valued item collides cmdk's selection highlight
                      // and search state across groups.
                      value={String(option.value)}
                      keywords={[option.label]}
                      disabled={option.disabled}
                      onSelect={() => handleToggle(option)}
                      data-testid={`${testId}-option-${option.value}`}
                    >
                      <CheckReadLinear
                        aria-hidden="true"
                        className={cn('size-4', isSelected ? 'opacity-100' : 'opacity-0')}
                      />
                      {/* No truncate: labels wrap once the content cap is hit. */}
                      <span className="min-w-0 break-words">{option.content ?? option.label}</span>
                      {option.trailing != null && (
                        <span className="ms-auto shrink-0 ps-2 text-2xs">{option.trailing}</span>
                      )}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            ))}
          </CommandList>
          {/* Footer rows sit OUTSIDE the scrollable CommandList so they stay
              pinned at the popover bottom whenever present (J22-B01). */}
          {/* Non-interactive truncation hint — a plain div (no cmdk value,
              no keyboard focus, no selection), separated from the options by
              a top border. */}
          {showMoreHint && (
            <div
              className="border-t border-border px-2 py-1.5 text-2xs text-muted-foreground text-start"
              data-testid={`${testId}-more-hint`}
            >
              {showCountForm
                ? t('APMultiSelect.showing_count', { shown: shownCount, total })
                : t('APMultiSelect.search_for_more')}
            </div>
          )}
          {/* Non-interactive loading row — same slot/border as the more-hint
              footer; shown while the list query resolves after a seeded
              option is already visible (else the short list looks complete). */}
          {showLoadingRow && (
            <div
              className="flex items-center gap-2 border-t border-border px-2 py-1.5 text-2xs text-muted-foreground text-start"
              data-testid={`${testId}-loading`}
            >
              <Spinner className="size-3.5" />
              {t('CommonText.loading')}
            </div>
          )}
        </Command>
      </PopoverContent>
    </Popover>
  );
}

/** RHF field wrapper for a SINGLE-value combobox — the form value is a scalar
 *  (`string | number | null`), adapted onto APMultiSelect `multiple={false}`.
 *  The async single-select successor to v3's APSelect-based pickers. */
export function APFormCombobox<TFieldValues extends FieldValues = FieldValues>({
  name,
  label,
  description,
  rules,
  variant = 'floating',
  required,
  disabled,
  className,
  'data-testid': testId,
  onValueChange,
  ...multiSelectProps
}: APFieldBaseProps<TFieldValues> &
  Omit<
    APMultiSelectProps,
    'value' | 'onChange' | 'multiple' | 'className' | 'data-testid' | 'floatingLabel'
  > & {
    /** Intercept the value change before it lands in the form. */
    onValueChange?: (value: string | number | null, commit: (value: unknown) => void) => void;
  }) {
  const { control } = useFormContext<TFieldValues>();
  const floating = variant === 'floating' && !!label;
  return (
    <FormField
      control={control}
      name={name as FieldPath<TFieldValues>}
      rules={rules}
      render={({ field }) => (
        <APFieldShell
          label={label}
          description={description}
          className={className}
          testId={fieldTestId(testId, name)}
          variant={variant}
          required={required ?? !!rules?.required}
          floatingMode="group"
        >
          <FormControl>
            <APMultiSelect
              {...multiSelectProps}
              multiple={false}
              floatingLabel={floating}
              value={
                field.value !== undefined && field.value !== null && field.value !== ''
                  ? [field.value as string | number]
                  : []
              }
              onChange={(values) => {
                const next = values[0] ?? null;
                if (onValueChange) {
                  onValueChange(next, field.onChange);
                } else {
                  field.onChange(next);
                }
              }}
              disabled={disabled}
              data-testid={`${fieldTestId(testId, name)}-control`}
            />
          </FormControl>
        </APFieldShell>
      )}
    />
  );
}

/** RHF field wrapper — the form value is `Array<string | number>`. */
export function APFormMultiSelect<TFieldValues extends FieldValues = FieldValues>({
  name,
  label,
  description,
  rules,
  variant = 'floating',
  required,
  disabled,
  className,
  'data-testid': testId,
  ...multiSelectProps
}: APFieldBaseProps<TFieldValues> &
  Omit<APMultiSelectProps, 'value' | 'onChange' | 'className' | 'data-testid' | 'floatingLabel'> & {
    /** Intercept the value change before it lands in the form. */
    onValuesChange?: (values: Array<string | number>, commit: (values: unknown) => void) => void;
  }) {
  const { control } = useFormContext<TFieldValues>();
  const { onValuesChange, ...rest } = multiSelectProps;
  const floating = variant === 'floating' && !!label;
  return (
    <FormField
      control={control}
      name={name as FieldPath<TFieldValues>}
      rules={rules}
      render={({ field }) => (
        <APFieldShell
          label={label}
          description={description}
          className={className}
          testId={fieldTestId(testId, name)}
          variant={variant}
          required={required ?? !!rules?.required}
          floatingMode="group"
        >
          <FormControl>
            <APMultiSelect
              {...rest}
              floatingLabel={floating}
              value={(field.value as Array<string | number> | undefined) ?? []}
              onChange={(values) => {
                if (onValuesChange) {
                  onValuesChange(values, field.onChange);
                } else {
                  field.onChange(values);
                }
              }}
              disabled={disabled}
              data-testid={`${fieldTestId(testId, name)}-control`}
            />
          </FormControl>
        </APFieldShell>
      )}
    />
  );
}
